import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from "react-hot-toast";
import { getProductConfigurationJsonApi } from '@/features/product-configurator/api/productConfiguration';
import { getAccountProductConfigApi } from '../api/dataverse';
import { transformConfigWithPricing } from '@/features/assistant/utils/configTransform';
import { IAssistantConfig } from '@/features/assistant/types';

import {
  closeQuoteApi,
  createOrderApi,
  createQuoteApi,
  createQuoteProductLine,
  createQuoteResponseApi,
  getAllQuoteCountsByStatusApi,
  getFollowUpChoiceCountApi,
  getQuoteCountByStatusApi,
  getQuoteDetailFromDataverse,
  getQuoteImageApi,
  getQuoteListFromDataverse,
  getQuoteResponseDetailsApi,
  getTotalQuotesCountApi,
  getTotalQuotesValueApi,
  updateQuote,
  uploadQuoteImageApi,
  getActivityLogsApi,
  getQuoteLineItemsApi,
  getSubQuoteProductsApi,
  createNoteApi,
  getNotesApi,
  type ICloseQuotePayload,
  type ICreateOrderPayload,
  type ICreateNotePayload,
} from "../api/dataverse";
import { queryKey } from '../constants';
import { IQuoteQueryParams } from "../types";

/**
 * Hook to fetch quote list with Dataverse API - no mapping
 */
export const useQuoteList = (params: { queryParams: IQuoteQueryParams }) => {
  return useQuery({
    queryKey: [queryKey.getQuoteList, params.queryParams],
    queryFn: () => getQuoteListFromDataverse(params.queryParams),
  });
};




/**
 * Hook to update quote (including status changes) using Dataverse API
 */
export const useUpdateQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { id: string; payload: any }) => updateQuote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteDetail] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteStatistics] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getAllQuoteCountsByStatus] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getTotalQuotesCount] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getTotalQuotesValue] });
      toast.success('Quote updated successfully');
    },
  });
};

/**
 * Hook to create a note attached to a quote (or other entity) record
 */
export const useCreateNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ICreateNotePayload) => createNoteApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteDetail] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getActivityLogs] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getNotes] });
      toast.success('Note added successfully');
    },
  });
};

/**
 * Hook to fetch notes attached to a quote (or other entity) record
 */
export const useGetNotes = (entityType: string, recordId: string | undefined) => {
  return useQuery({
    queryKey: [queryKey.getNotes, entityType, recordId],
    queryFn: () => {
      if (!recordId) throw new Error('Record ID is required');
      return getNotesApi(entityType, recordId);
    },
    enabled: !!recordId,
  });
};

/**
 * Hook to convert quote to order using Dataverse API
 */
export const useConvertQuoteToOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ICreateOrderPayload) => createOrderApi(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteDetail] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteStatistics] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getAllQuoteCountsByStatus] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getTotalQuotesCount] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getTotalQuotesValue] });
      toast.success(`Quote converted to order successfully! Order ID: ${data.orderId}`);
    },
  });
};


/**
 * Hook to get quote count by status code (single status)
 */
export const useQuoteCountByStatus = (statuscode?: number) => {
  return useQuery({
    queryKey: [queryKey.getQuoteCountByStatus, statuscode],
    queryFn: () => getQuoteCountByStatusApi(statuscode),
  });
};

/**
 * Hook to get all quote counts grouped by status code
 */
export const useAllQuoteCountsByStatus = (region?: number | null) => {
  return useQuery({
    queryKey: [queryKey.getAllQuoteCountsByStatus, region],
    queryFn: () => getAllQuoteCountsByStatusApi(region),
  });
};

export const useFollowUpChoiceCount = (region?: number | null) => {
  return useQuery({
    queryKey: ['quotes-followup-choice-count', region],
    queryFn: () => getFollowUpChoiceCountApi(region),
  });
};

/**
 * Hook to get quote response details
 */
export const useGetQuoteResponseDetails = (fileName?: string) => {
  return useQuery({
    queryKey: [queryKey.getQuoteResponseDetails, fileName],
    queryFn: () => getQuoteResponseDetailsApi({ fileName }),
    enabled: !!fileName,
  });
};

/**
 * Hook to create a new quote (creates as Draft then immediately patches to Live)
 */
export const useCreateQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: any) => {
      const result = await createQuoteApi({ ...payload, statuscode: 736000002, statecode: 0 });
      const quoteId = (result as any)?.quoteid ?? (result as any)?.id;
      if (quoteId) {
        await updateQuote({ id: quoteId, payload: { statuscode: 2, statecode: 1 } });
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteStatistics] });
      toast.success("Quote created successfully");
    },
  });
};

/**
 * Hook to create a quote response
 */
export const useCreateQuoteResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: any) => createQuoteResponseApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteList] });
      toast.success("Quote response created successfully");
    },
  });
};

/**
 * Hook to create quote product line
 */
export const useCreateQuoteProductLine = () => {
  return useMutation({
    mutationFn: createQuoteProductLine,
  });
};

/**
 * Hook to upload quote image
 */
export const useUploadQuoteImage = () => {
  return useMutation({
    mutationFn: ({
      imageData,
      quoteGuid,
      contentType,
    }: {
      imageData: File | Blob | ArrayBuffer;
      quoteGuid: string;
      contentType?: string;
    }) => uploadQuoteImageApi(imageData, quoteGuid, contentType),
    onSuccess: () => {
      toast.success("Quote image uploaded successfully");
    },
  });
};

/**
 * Hook to get quote images
 */
export const useGetQuoteImage = (quoteGuid?: string) => {
  return useQuery({
    queryKey: [queryKey.getQuoteImage, quoteGuid],
    queryFn: async () => {
      if (!quoteGuid) return null;
      const response = await getQuoteImageApi(quoteGuid);
      return response;
    },
    enabled: !!quoteGuid,
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to get total quotes count
 */
export const useTotalQuotesCount = (region?: number | null) => {
  return useQuery({
    queryKey: [queryKey.getTotalQuotesCount, region],
    queryFn: () => getTotalQuotesCountApi(region),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to get total quotes value
 */
export const useTotalQuotesValue = (region?: number | null) => {
  return useQuery({
    queryKey: [queryKey.getTotalQuotesValue, region],
    queryFn: () => getTotalQuotesValueApi(region),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * Hook to close quote with status and reason
 */
export const useCloseQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ICloseQuotePayload) => closeQuoteApi(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteList] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteDetail] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteStatistics] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getAllQuoteCountsByStatus] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getTotalQuotesCount] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getTotalQuotesValue] });
      toast.success('Quote closed successfully');
    },
  });
};

/**
 * Hook to fetch single quote detail from Dataverse
 */
export const useQuoteDetailFromDataverse = (quoteId: string | undefined) => {
  return useQuery({
    queryKey: [queryKey.getQuoteDetail, quoteId],
    queryFn: () => {
      if (!quoteId) throw new Error('Quote ID is required');
      return getQuoteDetailFromDataverse(quoteId);
    },
    enabled: !!quoteId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch activity logs for a specific quote
 */
export const useQuoteActivityLogs = (quoteId: string | undefined) => {
  return useQuery({
    queryKey: [queryKey.getActivityLogs, quoteId],
    queryFn: () => {
      if (!quoteId) throw new Error('Quote ID is required');
      return getActivityLogsApi(quoteId);
    },
    enabled: !!quoteId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to export all quotes matching the current filter as CSV data
 */
export const useExportQuotes = () => {
  return useMutation({
    mutationFn: ({ quoteFilter, subProductFilter }: { quoteFilter?: string; subProductFilter?: string }) =>
      getQuoteListFromDataverse({ current: 1, pageSize: 5000, quoteFilter, subProductFilter }),
  });
};

/**
 * Hook to get quote line items (quote details) for a specific quote
 */
export const useGetQuoteLineItems = (quoteId: string | undefined) => {
  return useQuery({
    queryKey: [queryKey.getQuoteLineItems, quoteId],
    queryFn: () => {
      if (!quoteId) throw new Error('Quote ID is required');
      return getQuoteLineItemsApi(quoteId);
    },
    enabled: !!quoteId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to get sub quote products for a specific quote product/detail
 */
export const useGetSubQuoteProducts = (quoteProductId: string | undefined) => {
  return useQuery({
    queryKey: [queryKey.getSubQuoteProducts, quoteProductId],
    queryFn: () => {
      if (!quoteProductId) throw new Error('Quote product ID is required');
      return getSubQuoteProductsApi(quoteProductId);
    },
    enabled: !!quoteProductId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch the chatbot configuration JSON and transform it to IAssistantConfig
 * Same pattern as react-web's useGetConfigurationJson — returns IAssistantConfig directly
 */
export const useGetConfigurationJson = () => {
  return useQuery<IAssistantConfig>({
    queryKey: ['quote-chatbot-config'],
    queryFn: async () => {
      const raw = await getProductConfigurationJsonApi();
      return transformConfigWithPricing(raw.Config as unknown as IAssistantConfig);
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useGetAccountProductConfig = (accountId?: string) => {
  return useQuery<IAssistantConfig>({
    queryKey: ['account-product-config', accountId],
    queryFn: async () => {
      const data = await getAccountProductConfigApi(accountId!);
      const transformed = transformConfigWithPricing(data?.Config ?? data) as IAssistantConfig;
      transformed.discountConfig = data?.discountConfig;
      return transformed;
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
  });
};
